import React, { createContext, useContext, useState, useEffect } from 'react';
import httpClient from '../api/httpClient';
import API_CONFIG from '../api/config';

const ConfigContext = createContext(null);

// Map subdomain prefix → backend sport enum value
// Add new sports here as subdomains are launched
const SUBDOMAIN_SPORT_MAP = {
  football:   'FOOTBALL',
  badminton:  'BADMINTON',
  volleyball: 'VOLLEYBALL',
  running:    'LONG_DISTANCE_RUNNING',
  cricket:    'CRICKET',
  tennis:     'TENNIS',
  basketball: 'BASKETBALL',
  hockey:     'HOCKEY',
  swimming:   'SWIMMING',
  kabaddi:    'KABADDI',
};

// Detect sport subdomain at module load time.
// e.g. "football.kridaclub.com" → "FOOTBALL", "kridaclub.com" → null
function detectSportFilter() {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  // Treat as sport subdomain only if there are ≥2 parts and the first part isn't "www" or "localhost"
  if (parts.length >= 2 && parts[0] !== 'www' && parts[0] !== 'localhost') {
    return SUBDOMAIN_SPORT_MAP[parts[0]] || null;
  }
  return null;
}

// Computed once at module load — stable for the lifetime of the page
const SPORT_FILTER = detectSportFilter();

// Fallback values so the app works even if the config API is unreachable
const FALLBACK = {
  sportOptions: [
    { value: 'FOOTBALL', label: 'Football' },
    { value: 'VOLLEYBALL', label: 'Volleyball' },
    { value: 'BADMINTON', label: 'Badminton' },
    { value: 'LONG_DISTANCE_RUNNING', label: 'Distance Running' },
  ],
  allSports: [],
  eventTypeOptions: [
    { value: 'TOURNAMENT', label: 'Tournament' },
    { value: 'LEAGUE', label: 'League' },
    { value: 'MATCH', label: 'Match' },
    { value: 'ONE_OFF', label: 'One Off' },
    { value: 'CHALLENGE', label: 'Challenge' },
  ],
  eventLevelOptions: [
    { value: 'JUNIOR', label: 'Junior' },
    { value: 'AMATEUR', label: 'Amateur' },
    { value: 'PROFESSIONAL', label: 'Professional' },
  ],
  sessionTypeOptions: [
    { value: 'GROUP', label: 'Group' },
    { value: 'INDIVIDUAL', label: 'Individual' },
    { value: 'CAMP', label: 'Camp' },
  ],
  feePeriodOptions: [
    { value: 'PER_SESSION', label: 'Per Session' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'FULL_COURSE', label: 'Full Course' },
  ],
};

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(FALLBACK);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    httpClient.get(API_CONFIG.ENDPOINTS.CONFIG)
      .then((res) => {
        if (cancelled) return;
        const data = res.data || res;
        const sportOptions = (data.sports || [])
          .filter((s) => s.active)
          .map((s) => ({ value: s.value, label: s.label }));
        const allSports = (data.sports || []).map((s) => ({
          value: s.value,
          label: s.label,
          active: s.active,
        }));
        setConfig({
          sportOptions,
          allSports,
          eventTypeOptions: (data.eventTypes || []).map((e) => ({ value: e.value, label: e.label })),
          eventLevelOptions: (data.eventLevels || []).map((e) => ({ value: e.value, label: e.label })),
          sessionTypeOptions: (data.sessionTypes || []).map((e) => ({ value: e.value, label: e.label })),
          feePeriodOptions: (data.feePeriods || []).map((e) => ({ value: e.value, label: e.label })),
        });
      })
      .catch(() => {
        // Use fallback on error — app still works
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    // sportFilter is stable (computed once from hostname) — not part of async config
    <ConfigContext.Provider value={{ ...config, loaded, sportFilter: SPORT_FILTER }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error('useConfig must be used within a ConfigProvider');
  return ctx;
}
